import { createContext, useContext, useMemo, useState } from 'react';
import {
    Background,
    BaseEdge,
    Controls,
    EdgeLabelRenderer,
    getBezierPath,
    Handle,
    Panel,
    Position,
    ReactFlow,
    ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
    ActionDependant,
    evaluateTemplateValue,
    GlobalDataContext,
    TemplateContext,
} from '@ea-lab/reactive-json';

// HTTP status family → colour. Kept in sync with FAMILY_COLORS in
// wsolutionCallsProcessor.js (which colours the proportion bar segments).
const FAMILY_COLORS = { 2: '#10b981', 3: '#3b82f6', 4: '#f59e0b', 5: '#ef4444' };
const FAMILY_LABELS = { 2: '2xx — Succès', 3: '3xx — Redirection', 4: '4xx — Client', 5: '5xx — Serveur' };

// ---------------------------------------------------------------------------
// Layout constants — two side columns flanking a central "site" hub node.
// ---------------------------------------------------------------------------
const ROW_GAP   = 120;   // vertical spacing between side nodes
const RIGHT_X   = 620;   // x of the right column
const CENTER_X  = 310;   // x of the central hub node
const EDGE_MIN_W = 1.5;  // px — thinnest edge (lowest call volume)
const EDGE_MAX_W = 9;    // px — thickest edge (highest call volume)
const CALLER_X  = -330;  // x of the incoming callers column, left of the paths
const CALLER_GAP = 72;   // vertical spacing between caller nodes (they are shorter)
const FLOW_COLOR = '#38bdf8';       // caller → path edges (sky-400, the incoming hue)
const MUTED_FLOW_COLOR = '#cbd5e1'; // edges of the grouped « Autres » nodes (slate-300)
const DIMMED_OPACITY = 0.15;        // edges NOT leaving the hovered caller
const OPACITY_TRANSITION = 'opacity 150ms ease-out';

// Survol d'un appelant : son identifiant de nœud, posé par le nœud lui-même et lu
// par le graphe pour faire ressortir SES flèches. Un contexte plutôt que
// onNodeMouseEnter de ReactFlow : il sert aussi le focus clavier, que ReactFlow ne
// remonte pas.
const HighlightContext = createContext(null);

// ---------------------------------------------------------------------------
// Custom nodes
// ---------------------------------------------------------------------------

/** Central hub — represents the site / content emitting the web-service calls. */
function SiteNode({ data }) {
    return (
        <div className="relative rounded-xl border-2 border-indigo-500 bg-indigo-50 shadow-lg px-5 py-4 min-w-[180px] text-center">
            {/* Deux poignées par côté : le site est la SOURCE d'un appel sortant
                et la CIBLE d'un appel entrant, et une même poignée ne peut pas
                jouer les deux rôles. */}
            <Handle id="left"   type="source" position={Position.Left}  style={{ opacity: 0 }} isConnectable={false} />
            <Handle id="right"  type="source" position={Position.Right} style={{ opacity: 0 }} isConnectable={false} />
            <Handle id="tleft"  type="target" position={Position.Left}  style={{ opacity: 0 }} isConnectable={false} />
            <Handle id="tright" type="target" position={Position.Right} style={{ opacity: 0 }} isConnectable={false} />
            <div className="text-[10px] font-semibold uppercase tracking-wider text-indigo-400">Site</div>
            <div className="font-bold text-indigo-800 text-sm mt-0.5 break-words">{data.label}</div>
            {data.total != null && (
                <div className="text-xs text-indigo-500 mt-1">
                    {data.total.toLocaleString('fr-FR')} appels
                </div>
            )}
        </div>
    );
}

/** A web-service node, sitting on one of the two side columns. */
function WsNode({ data }) {
    return (
        <div
            className={`relative rounded-lg border bg-white shadow-sm px-3 py-2.5 w-[230px] ${
                data.incoming ? 'border-sky-300 ring-1 ring-sky-100' : 'border-gray-200'
            }`}
        >
            <Handle id="tleft"  type="target" position={Position.Left}  style={{ opacity: 0 }} isConnectable={false} />
            <Handle id="tright" type="target" position={Position.Right} style={{ opacity: 0 }} isConnectable={false} />
            {/* Un webservice ENTRANT émet vers le site : il lui faut aussi des
                poignées source, sinon l'arête inversée n'a nulle part où naître. */}
            <Handle id="sleft"  type="source" position={Position.Left}  style={{ opacity: 0 }} isConnectable={false} />
            <Handle id="sright" type="source" position={Position.Right} style={{ opacity: 0 }} isConnectable={false} />

            <div className="flex items-center gap-1.5 min-w-0">
                <span
                    className={`shrink-0 inline-flex items-center rounded px-1 py-px text-[9px] font-bold uppercase tracking-wider ${
                        data.incoming ? 'bg-sky-100 text-sky-700' : 'bg-gray-100 text-gray-500'
                    }`}
                    title={data.incoming
                        ? 'Appels ENTRANTS : le site est la destination'
                        : 'Appels SORTANTS : le site est l’appelant'}
                >
                    {data.directionLabel}
                </span>
                {/* Lien ÉTIRÉ : son ::after couvre toute la carte, donc un clic n'importe
                    où mène au tableau de bord, au clavier compris. Pas de <a> englobant la
                    carte : il contiendrait le lien des logs, ce que HTML interdit. */}
                <a
                    href={data.dashboardHref}
                    className="nodrag after:absolute after:inset-0 after:rounded-lg focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-indigo-500 font-medium text-gray-800 text-sm truncate hover:text-indigo-700"
                    title={data.wsid}
                >
                    {data.label}<span className="sr-only"> — tableau de bord</span>
                </a>
            </div>
            <div className="flex items-center justify-between text-xs text-gray-500 mt-0.5">
                <span className="font-semibold text-gray-700">{data.countFormatted} appel{data.count > 1 ? 's' : ''}</span>
                <span>{data.avgFormatted}</span>
            </div>

            {/* Status-code proportion bar (reuses the processor's segments). */}
            <div className="flex h-2.5 w-full rounded overflow-hidden bg-gray-100 ring-1 ring-gray-200 mt-2">
                {data.families.map((seg) => (
                    <div key={seg.key} className="h-full" style={seg.style} title={seg.title} />
                ))}
            </div>

            {/* Lien secondaire, AU-DESSUS du lien étiré de la carte (relative z-10). */}
            <div className="flex items-center justify-end mt-2">
                <a
                    href={data.logsHref}
                    className="nodrag relative z-10 text-[11px] font-medium text-gray-400 hover:text-blue-600 transition-colors"
                >
                    👁 Logs<span className="sr-only"> — {data.label}</span>
                </a>
            </div>
        </div>
    );
}

/**
 * Un appelant d'un webservice entrant : l'URL référente, ou l'IP à défaut.
 * Nœud compact et sans liens : il ne désigne pas une destination, il en
 * alimente une (le chemin appelé, colonne suivante).
 */
function CallerNode({ id, data }) {
    const other = data.kind === 'other';
    const highlight = useContext(HighlightContext);
    const on = () => highlight?.(id);
    const off = () => highlight?.(null);
    return (
        <div
            onMouseEnter={on}
            onMouseLeave={off}
            onFocus={on}
            onBlur={off}
            className={`relative rounded-lg border bg-white shadow-sm px-3 py-2 w-[230px] hover:shadow-md transition-shadow ${
                other ? 'border-dashed border-gray-300' : 'border-sky-200'
            }`}
        >
            <Handle id="sright" type="source" position={Position.Right} style={{ opacity: 0 }} isConnectable={false} />
            <div className="flex items-center gap-1.5 min-w-0">
                <span className={`shrink-0 inline-flex items-center rounded px-1 py-px text-[9px] font-bold uppercase tracking-wider ${
                    other ? 'bg-gray-100 text-gray-500' : 'bg-sky-100 text-sky-700'
                }`}>
                    {{ referer: 'URL', addr: 'IP', other: 'Autres' }[data.kind]}
                </span>
                <a
                    href={data.dashboardHref}
                    className={`nodrag after:absolute after:inset-0 after:rounded-lg focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-indigo-500 text-sm truncate hover:text-indigo-700 ${other ? 'text-gray-500 italic' : 'font-medium text-gray-800'}`}
                    title={data.title}
                >
                    {data.label}<span className="sr-only"> — tableau de bord</span>
                </a>
            </div>
            <div className="flex items-center justify-between text-xs text-gray-500 mt-0.5">
                <span>{data.countFormatted} appel{data.count > 1 ? 's' : ''}</span>
                <a
                    href={data.logsHref}
                    className="nodrag relative z-10 text-[11px] font-medium text-gray-400 hover:text-blue-600 transition-colors"
                >
                    👁 Logs<span className="sr-only"> — {data.label}</span>
                </a>
            </div>
        </div>
    );
}

const NODE_TYPES = { site: SiteNode, ws: WsNode, caller: CallerNode };

/**
 * Hub edge — bezier line ending at the *base* of a hand-drawn arrowhead rather
 * than at its tip. The default ReactFlow marker draws the triangle on top of a
 * line that still runs to the very point, so a thick stroke spills out around
 * the narrowing tip. Here the line stops short by the arrowhead length and the
 * filled triangle bridges the gap — no overshoot at any stroke width.
 *
 * Edges in this layout always approach a side node horizontally (target handle
 * Left or Right), so the arrow direction is purely ±x, which keeps the geometry
 * trivial.
 */
function HubEdge({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, style = {}, data }) {
    const color = style.stroke ?? '#94a3b8';
    const sw = style.strokeWidth ?? 1.5;
    const opacity = style.opacity ?? 1;
    const arrowLen = 5 + sw * 1.1;        // triangle depth
    const arrowHalf = arrowLen * 0.62;    // triangle half-base (always > sw/2)

    // targetPosition Left  → edge enters the node's left side → arrow points right (+x)
    // targetPosition Right → enters the right side            → arrow points left  (-x)
    const dir = targetPosition === Position.Left ? 1 : -1;
    const baseX = targetX - dir * arrowLen; // where the visible line stops

    const [edgePath, labelX, labelY] = getBezierPath({
        sourceX, sourceY, sourcePosition,
        targetX: baseX, targetY,
        targetPosition,
    });

    const triangle = `M ${targetX},${targetY} L ${baseX},${targetY - arrowHalf} L ${baseX},${targetY + arrowHalf} Z`;

    return (
        <>
            <BaseEdge path={edgePath} style={{ stroke: color, strokeWidth: sw, strokeLinecap: 'butt', opacity, transition: OPACITY_TRANSITION }} />
            <path d={triangle} fill={color} stroke="none" style={{ opacity, transition: OPACITY_TRANSITION }} />
            {data?.label != null && (!data.quiet || data.lit) && (
                <EdgeLabelRenderer>
                    <div
                        style={{ transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`, opacity, transition: OPACITY_TRANSITION }}
                        className="absolute pointer-events-none rounded bg-white/85 px-1 py-0.5 text-[10px] font-semibold text-slate-600"
                    >
                        {data.label}
                    </div>
                </EdgeLabelRenderer>
            )}
        </>
    );
}

const EDGE_TYPES = { hub: HubEdge };

// ---------------------------------------------------------------------------
// Graph construction
// ---------------------------------------------------------------------------

/** Maps a raw call volume to an edge stroke width, relative to the busiest one. */
function edgeWidth(count, maxCount) {
    if (!maxCount) return EDGE_MIN_W;
    // sqrt scale so low-volume services stay visible next to very busy ones.
    const ratio = Math.sqrt(count) / Math.sqrt(maxCount);
    return EDGE_MIN_W + (EDGE_MAX_W - EDGE_MIN_W) * ratio;
}

// Linear interpolation between two #rrggbb colours.
function lerpHex(a, b, t) {
    const pa = [1, 3, 5].map((i) => parseInt(a.slice(i, i + 2), 16));
    const pb = [1, 3, 5].map((i) => parseInt(b.slice(i, i + 2), 16));
    const ch = pa.map((v, i) => Math.round(v + (pb[i] - v) * t)
        .toString(16).padStart(2, '0'));
    return `#${ch.join('')}`;
}

/**
 * Colours an edge by its share of error responses (4xx + 5xx). 0 errors → green,
 * fully erroring → red, passing through amber at the halfway point.
 */
function errorColor(familyCounts, total) {
    if (!total) return '#94a3b8';
    const errors = (familyCounts?.[4] ?? 0) + (familyCounts?.[5] ?? 0);
    const ratio = Math.min(1, errors / total);
    return ratio <= 0.5
        ? lerpHex(FAMILY_COLORS[2], FAMILY_COLORS[4], ratio / 0.5)        // green → amber
        : lerpHex(FAMILY_COLORS[4], FAMILY_COLORS[5], (ratio - 0.5) / 0.5); // amber → red
}

/** Haut d'une colonne de `n` nœuds espacés de `gap`, centrée sur `centerY`. */
function columnTop(n, gap, centerY) {
    return centerY - ((n - 1) * gap) / 2;
}

/**
 * Un webservice ENTRANT se déplie en deux colonnes : ses appelants (l'URL
 * référente, ou l'IP à défaut), puis les chemins appelés, seuls à rejoindre le
 * site.
 *
 * Côté entrant, l'hôte de destination est toujours le nôtre : l'afficher
 * répéterait le centre du graphe. Ce qui se lit, c'est QUI appelle et QUOI.
 * Chaque chemin garde les liens vers le tableau de bord et les logs de SON
 * webservice. Sans flux renvoyé par le serveur, repli sur le webservice
 * lui-même : mieux vaut un nœud au nom redondant qu'une branche qui disparaît.
 */
function unfoldIncoming(rows) {
    const callers = [];
    const paths = [];
    const flows = [];

    rows.forEach((row, w) => {
        const flow = row.incomingFlow;
        if (!flow) {
            paths.push({ id: `in-${w}`, data: row });
            return;
        }

        const callerId = new Map(flow.callers.map((c, i) => [c.key, `in-${w}-c${i}`]));
        const pathId = new Map(flow.paths.map((p, i) => [p.key, `in-${w}-p${i}`]));
        const otherCallers = new Set(flow.callers.filter((c) => c.kind === 'other').map((c) => c.key));

        flow.callers.forEach((c) => callers.push({ id: callerId.get(c.key), data: c }));
        flow.paths.forEach((p) => paths.push({
            id: pathId.get(p.key),
            data: {
                ...row,
                wsid: p.title,
                label: p.label,
                muted: !!p.muted,
                count: p.count,
                countFormatted: p.countFormatted,
                avgFormatted: p.avgFormatted,
                families: p.families,
                familyCounts: p.familyCounts,
                directionLabel: p.muted ? 'Autres' : 'Chemin',
                dashboardHref: p.dashboardHref ?? row.dashboardHref,
                logsHref: p.logsHref ?? row.logsHref,
            },
        }));
        flow.flows.forEach((f) => {
            const source = callerId.get(f.caller);
            const target = pathId.get(f.path);
            if (source && target) {
                flows.push({ source, target, count: f.count, muted: otherCallers.has(f.caller) });
            }
        });
    });

    return { callers, paths, flows };
}

/** Arête d'un webservice (ou d'un chemin) avec le site, colorée par ses erreurs. */
function hubEdge(id, data, ends, maxCount) {
    return {
        id: `e-${id}`,
        ...ends,
        type: 'hub',
        style: {
            stroke: data.muted ? MUTED_FLOW_COLOR : errorColor(data.familyCounts, data.count ?? 0),
            strokeWidth: edgeWidth(data.count ?? 0, maxCount),
        },
        data: { label: data.countFormatted },
    };
}

function buildGraph(rows, siteLabel) {
    // Le total du centre se compte sur les WEBSERVICES, pas sur les nœuds : un
    // webservice entrant se déplie en appelants ET en chemins, qui comptent
    // chacun les mêmes appels.
    const totalCalls = rows.reduce((s, r) => s + (r.count ?? 0), 0);

    const incoming = unfoldIncoming(rows.filter((r) => r.incoming));
    const outgoing = rows.filter((r) => !r.incoming);

    // Sans appel entrant, les webservices s'alternent de part et d'autre du
    // site pour équilibrer le graphe. Avec, la gauche leur est réservée et se
    // lit dans le sens de l'appel : appelants → chemins → site → destinations.
    const hasIncoming = incoming.paths.length > 0;
    const left = hasIncoming
        ? incoming.paths
        : outgoing.filter((_, i) => i % 2 === 0).map((data, i) => ({ id: `ws-left-${i}`, data }));
    const right = (hasIncoming ? outgoing : outgoing.filter((_, i) => i % 2 === 1))
        .map((data, i) => ({ id: `ws-right-${i}`, data }));

    const centerY = Math.max(
        0,
        (left.length - 1) * ROW_GAP,
        (right.length - 1) * ROW_GAP,
        (incoming.callers.length - 1) * CALLER_GAP,
    ) / 2;

    // L'épaisseur se rapporte à la plus grosse arête RÉELLEMENT tracée : la
    // rapporter au total d'un webservice écraserait ses flux au minimum.
    const maxCount = Math.max(
        0,
        ...left.map((n) => n.data.count ?? 0),
        ...right.map((n) => n.data.count ?? 0),
        ...incoming.flows.map((f) => f.count),
    );

    const nodes = [
        {
            id: '__site__',
            type: 'site',
            position: { x: CENTER_X, y: centerY },
            data: { label: siteLabel, total: totalCalls },
            draggable: false,
        },
    ];
    const edges = [];

    const leftTop = columnTop(left.length, ROW_GAP, centerY);
    left.forEach((n, i) => {
        nodes.push({ id: n.id, type: 'ws', position: { x: 0, y: leftTop + i * ROW_GAP }, data: n.data });
        // Un appel entrant part du chemin et arrive sur le site : la flèche
        // s'inverse, donc source et cible aussi, sur les faces qui se font face.
        edges.push(hubEdge(n.id, n.data, n.data.incoming
            ? { source: n.id, target: '__site__', sourceHandle: 'sright', targetHandle: 'tleft' }
            : { source: '__site__', target: n.id, sourceHandle: 'left', targetHandle: 'tright' }, maxCount));
    });

    const rightTop = columnTop(right.length, ROW_GAP, centerY);
    right.forEach((n, i) => {
        nodes.push({ id: n.id, type: 'ws', position: { x: RIGHT_X, y: rightTop + i * ROW_GAP }, data: n.data });
        edges.push(hubEdge(n.id, n.data,
            { source: '__site__', target: n.id, sourceHandle: 'right', targetHandle: 'tleft' }, maxCount));
    });

    const callerTop = columnTop(incoming.callers.length, CALLER_GAP, centerY);
    incoming.callers.forEach((n, i) => {
        nodes.push({ id: n.id, type: 'caller', position: { x: CALLER_X, y: callerTop + i * CALLER_GAP }, data: n.data });
    });
    incoming.flows.forEach((f, i) => {
        edges.push({
            id: `f-${i}`,
            source: f.source,
            target: f.target,
            sourceHandle: 'sright',
            targetHandle: 'tleft',
            type: 'hub',
            style: { stroke: f.muted ? MUTED_FLOW_COLOR : FLOW_COLOR, strokeWidth: edgeWidth(f.count, maxCount) },
            // Étiquette DISCRÈTE : jusqu'à 7 × 7 flèches appelant → chemin, leurs chiffres
            // se chevauchaient en un amas illisible (mesuré sur 50 000 appels). Ils ne
            // s'affichent qu'au survol de l'appelant, avec ses flèches.
            data: { label: f.count.toLocaleString('fr-FR'), quiet: true },
        });
    });

    return { nodes, edges };
}

// ---------------------------------------------------------------------------
// Graph view
// ---------------------------------------------------------------------------

function WsGraph({ rows, siteLabel }) {
    const { nodes, edges } = useMemo(() => buildGraph(rows, siteLabel), [rows, siteLabel]);

    // Survol (ou focus) d'un appelant : les flèches qui en PARTENT restent pleines et
    // passent au premier plan, toutes les autres pâlissent.
    const [hovered, setHovered] = useState(null);
    const shownEdges = useMemo(() => (hovered == null ? edges : edges.map((e) => {
        const lit = e.source === hovered;
        return { ...e, zIndex: lit ? 10 : 0, style: { ...e.style, opacity: lit ? 1 : DIMMED_OPACITY }, data: { ...e.data, lit } };
    })), [edges, hovered]);

    return (
        <div className="h-[70vh] w-full rounded-lg border border-gray-200 bg-gray-50 shadow-sm overflow-hidden">
            <HighlightContext.Provider value={setHovered}>
            <ReactFlowProvider>
                <ReactFlow
                    nodes={nodes}
                    edges={shownEdges}
                    nodeTypes={NODE_TYPES}
                    edgeTypes={EDGE_TYPES}
                    fitView
                    fitViewOptions={{ padding: 0.15 }}
                    nodesConnectable={false}
                    proOptions={{ hideAttribution: true }}
                    minZoom={0.2}
                >
                    <Background gap={16} size={1} color="#e2e8f0" />
                    <Controls showInteractive={false} />

                    {/* Legend */}
                    <Panel position="top-right">
                        <div className="bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg shadow-sm px-3 py-2 text-xs text-gray-600 space-y-2">
                            {/* Status-code families — colour the proportion bars in the nodes. */}
                            <div>
                                <div className="font-semibold text-gray-700 mb-1.5">Codes HTTP</div>
                                <ul className="space-y-1">
                                    {[2, 3, 4, 5].map((f) => (
                                        <li key={f} className="flex items-center gap-2">
                                            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: FAMILY_COLORS[f] }} />
                                            {FAMILY_LABELS[f]}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Edge encoding. */}
                            <div className="border-t border-gray-100 pt-2">
                                <div className="font-semibold text-gray-700 mb-1">Flèches = taux d&apos;erreurs</div>
                                <div
                                    className="h-2.5 w-full rounded"
                                    style={{ background: `linear-gradient(to right, ${FAMILY_COLORS[2]}, ${FAMILY_COLORS[4]}, ${FAMILY_COLORS[5]})` }}
                                />
                                <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                                    <span>0 %</span>
                                    <span>50 %</span>
                                    <span>100 %</span>
                                </div>
                            </div>
                        </div>
                    </Panel>
                </ReactFlow>
            </ReactFlowProvider>
            </HighlightContext.Provider>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Public component — ReactiveJSON element plugin
//
// Renders *only* the ReactFlow hub-and-spoke graph (the part that can't be
// expressed declaratively). The view toggle and the list table live in YAML
// (see pages/wsolution/home.yaml).
//
// YAML usage:
//   - type: WsolutionCallsGraph
//     rows: ~~.appelData.rows
//     siteLabel: "Mon site"      # optional, default "Site"
// ---------------------------------------------------------------------------

export function WsolutionCallsGraph({ props }) {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);

    const rows = evaluateTemplateValue({
        valueToEvaluate: props?.rows,
        globalDataContext,
        templateContext,
    }) ?? [];

    const siteLabel = evaluateTemplateValue({
        valueToEvaluate: props?.siteLabel,
        globalDataContext,
        templateContext,
    }) ?? 'Site';

    return (
        <ActionDependant {...props}>
            {rows.length === 0
                ? <p className="text-center text-gray-400 py-12">Aucun appel enregistré.</p>
                : <WsGraph rows={rows} siteLabel={siteLabel} />}
        </ActionDependant>
    );
}
